import ClearIcon from '@mui/icons-material/Clear';
import {
  autocompleteClasses,
  AutocompleteProps,
  SvgIcon,
  svgIconClasses,
  type SvgIconProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { menuItem, paper } from '../../css';

const PopupIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path d="M12,16 C11.7663478,16.0004565 11.5399121,15.9190812 11.36,15.77 L5.36,10.77 C4.93474074,10.4165378 4.87653776,9.78525926 5.23,9.36 C5.58346224,8.93474074 6.21474074,8.87653776 6.64,9.23 L12,13.71 L17.36,9.39 C17.5665934,9.2222295 17.8315409,9.14373108 18.0961825,9.17188444 C18.3608241,9.2000378 18.6033268,9.33252029 18.77,9.54 C18.9551341,9.74785947 19.0452548,10.0234772 19.0186853,10.3005589 C18.9921158,10.5776405 18.8512608,10.8311099 18.63,11 L12.63,15.83 C12.444916,15.955516 12.2231011,16.0153708 12,16 Z" />
  </SvgIcon>
);

export default function Autocomplete(theme: Theme) {
  const rootStyles = (
    ownerState: AutocompleteProps<string, true, false, false>
  ) => {
    const defaultStyle = {
      borderRadius: theme.shape.borderRadius * 0.5,

      [`& > .${autocompleteClasses.tag}`]: {
        margin: '0!important',
      },
      '& > input': {
        padding: `${theme.spacing(0, 1, 0, 0)}!important`,
      },
    };

    const sizeStyle = {
      ...(ownerState.size === 'small' && {
        minHeight: 24,
        minWidth: 24,
        padding: `${theme.spacing(0.5, 6, 0.5, 1)}!important`,
        fontSize: theme.typography.body4.fontSize,
      }),
      ...(ownerState.size === 'medium' && {
        minHeight: 32,
        minWidth: 32,
        padding: `${theme.spacing(0.5, 6, 0.5, 1)}!important`,
        fontSize: theme.typography.body3.fontSize,
      }),
    };

    return { ...defaultStyle, ...sizeStyle };
  };

  return {
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <PopupIcon />,
        ChipProps: {
          size: 'small',
          shape: 'rounded',
          variant: 'outlined',
          deleteIcon: <ClearIcon sx={{ fontSize: '12px!important' }} />,
        },

        slotProps: {
          popupIndicator: {
            size: 'xsmall',
            sx: { color: theme.palette.grey[700] },
          },
          clearIndicator: {
            size: 'xsmall',
            sx: { color: theme.palette.grey[700] },
          },
          popper: {
            sx: {
              border: `1px solid ${theme.palette.primary[500]}`,
              borderRadius: theme.spacing(0.5),
              marginTop: `${theme.spacing(0.5)}!important`,
            },
          },
          paper: {
            sx: {
              '& .MuiAutocomplete-option[aria-selected="true"]': {
                position: 'relative',
                paddingRight: '32px',
                backgroundColor: 'transparent!important',
                color: theme.palette.primary[500],
                '&::after': {
                  content: '""',
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  position: 'absolute',
                  right: '8px',
                  background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ctitle%3Echeck%3C/title%3E%3Cpath fill='%231851EF' d='M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z'/%3E%3C/svg%3E")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  color: '#1851EF',
                },
              },
            },
          },
        },
      },

      styleOverrides: {
        inputRoot: ({
          ownerState,
        }: {
          ownerState: AutocompleteProps<string, true, false, false>;
        }) => rootStyles(ownerState),
        paper: {
          ...paper({ theme, dropdown: true }),
        },
        listbox: {
          padding: 0,
          [`& .${autocompleteClasses.option}`]: {
            ...menuItem(theme),
          },
          // Custom scrollbar styles
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.grey[200],
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.grey[400],
            borderRadius: '2px',
            transition: 'background-color 0.2s ease',
          },

          '&::-webkit-scrollbar-thumb:hover': {
            background: theme.palette.grey[600],
          },
        },
        endAdornment: {
          [`& .${svgIconClasses.root}`]: {
            width: 18,
            height: 18,
          },
        },
        noOptions: {
          color: theme.palette.grey[500],
          padding: theme.spacing(1),
          ...theme.typography.body3,
        },
      },
    },
  };
}
